import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { cloneDeep, flatten, set } from 'lodash';

import { errorHandler } from 'utils/ErrorHandler';
import { GET_SETTLEMENT_LIST_BY_FILTER } from 'containers/SettlementRequest/graphQL';
import {
  Settlement,
  SettlementsEdges,
  SettlementsNode,
} from 'containers/SettlementRequest/graphQL/types';
import { PageInfo } from 'types/api';

export interface ConvertSettlementList {
  totalCount: number;
  pageInfo: PageInfo;
  list: { title: string; edges: SettlementsEdges[] }[];
}

export function useQuerySettlementListByFilter() {
  const location = useLocation();
  const navigate = useNavigate();

  const { data, loading, refetch } = useQuery<Settlement>(
    GET_SETTLEMENT_LIST_BY_FILTER,
    {
      variables: {
        page: 1,
        size: 50,
        filter: {
          hostId: null,
          invoiceIds: null,
          taxIds: null,
          uncollected: null,
          unissued: null,
          term: null,
          hasMinusTotalPayment: null,
        },
      },
    },
  );

  const [settlementList, setSettlementList] = useState<ConvertSettlementList>();
  const [isShowRequireInvoice, setIsShowRequireInvoice] =
    useState<boolean>(false);
  const [selectedSettlement, setSelectedSettlement] = useState<
    SettlementsNode[]
  >([]);

  const convertTimestampToDate = timeStamp => {
    return dayjs(timeStamp).format('YYYY년 M월');
  };

  const convertingSettlementList = (
    data: Settlement,
  ): ConvertSettlementList => {
    const settlements = data.settlement.settlements;

    let result: ConvertSettlementList = {
      totalCount: settlements.totalCount,
      pageInfo: settlements.pageInfo,
      list: [],
    };

    for (let idx = 0; idx < data.settlement.settlements.edges.length; idx++) {
      const date = convertTimestampToDate(
        settlements.edges[idx].node.startedDate,
      );

      const existingEntryIndex = result.list.findIndex(
        item => item.title === date,
      );

      if (existingEntryIndex !== -1) {
        // 이미 존재하는 날짜일 경우 value에 새로운 데이터를 추가
        result.list[existingEntryIndex].edges.push(settlements.edges[idx]);
      } else {
        // 존재하지 않는 날짜일 경우 새로운 항목 추가
        result.list = [
          ...result.list,
          {
            title: date,
            edges: [settlements.edges[idx]],
          },
        ];
      }
    }

    const sortItem = cloneDeep(result).list.sort((a, b) => {
      return dayjs(b.edges[0].node.startedDate).diff(
        dayjs(a.edges[0].node.startedDate),
      );
    });

    result.list = sortItem;

    return result;
    // setSettlementList(result);
  };

  const handleShowRequireInvoice = () => {
    let copyData = cloneDeep(data);
    setSelectedSettlement([]);

    if (isShowRequireInvoice) {
      setIsShowRequireInvoice(false);

      const convertData = convertingSettlementList(copyData);

      setSettlementList(convertData);

      return;
    }

    setIsShowRequireInvoice(true);

    copyData.settlement.settlements.edges =
      copyData.settlement.settlements.edges.filter(
        item => item.node.invoice === null,
      );

    const convertData = convertingSettlementList(copyData);

    setSettlementList(convertData);
  };

  const refetchSettlementList = async (filter?: any) => {
    try {
      const response = await refetch({
        page: 1,
        size: 50,
        filter: filter
          ? filter
          : {
              hostId: null,
              invoiceIds: null,
              taxIds: null,
              uncollected: null,
              unissued: null,
              term: null,
              hasMinusTotalPayment: null,
            },
      });

      if (response) {
        setSelectedSettlement([]);
        const convertData = convertingSettlementList(response.data);

        if (isShowRequireInvoice) {
          const copyData = cloneDeep(response.data);
          copyData.settlement.settlements.edges =
            copyData.settlement.settlements.edges.filter(
              item => item.node.invoice === null,
            );

          const invoiceConvertData = convertingSettlementList(copyData);

          setIsShowRequireInvoice(true);
          setSettlementList(invoiceConvertData);

          return;
        }

        setIsShowRequireInvoice(false);
        setSettlementList(convertData);
        setSelectedSettlement([]);
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  // useEffect(() => {
  //   if (location.state && settlementList) {
  // const converSettlement = flatten(
  //   settlementList.list.map(item => item.edges.map(edge => edge.node)),
  // );

  //     if (isShowRequireInvoice) {
  //       setSelectedSettlement(converSettlement);
  //     }
  //   }
  // }, [location.state, settlementList]);

  useEffect(() => {
    if (data) {
      const convertData = convertingSettlementList(data);

      setSettlementList(convertData);

      if (location.state) {
        navigate(location.pathname, {
          replace: true,
          state: { isRequireInvoice: false },
        });

        let copyData = cloneDeep(data);

        if (location.state.isRequireInvoice) {
          copyData.settlement.settlements.edges =
            copyData.settlement.settlements.edges.filter(
              item => item.node.invoice === null,
            );

          const selectConvertData = convertingSettlementList(copyData);
          const convertSettlement = flatten(
            selectConvertData.list.map(item =>
              item.edges.map(edge => edge.node),
            ),
          );

          setSelectedSettlement(convertSettlement);

          setIsShowRequireInvoice(true);
          setSettlementList(selectConvertData);

          return;
        }

        handleShowRequireInvoice();
      }
    }
  }, [data]);

  return {
    data,
    settlementList,
    selectedSettlement,
    isLoading: loading,
    setSelectedSettlement,
    isShowRequireInvoice,
    refetchSettlementList,
    handleShowRequireInvoice,
  };
}
